import { initializeApp, getApps } from "firebase/app";
import {
  getAuth,
  connectAuthEmulator,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  linkWithCredential,
  signInWithPhoneNumber,
  RecaptchaVerifier,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

export const isFirebaseConfigured = () =>
  Boolean(firebaseConfig.apiKey && firebaseConfig.authDomain && firebaseConfig.projectId);

let app = null;
let auth = null;
let emulatorConnected = false;

const isAuthEmulatorEnabled = () =>
  process.env.REACT_APP_FIREBASE_USE_EMULATOR === "true" &&
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

export function getFirebaseAuth() {
  if (!isFirebaseConfigured()) {
    throw new Error("Firebase is not configured. Set REACT_APP_FIREBASE_* env vars.");
  }
  if (!app) {
    app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
  }
  if (!auth) {
    auth = getAuth(app);
    if (isAuthEmulatorEnabled() && !emulatorConnected) {
      const host = process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST || "http://127.0.0.1:9099";
      connectAuthEmulator(auth, host, { disableWarnings: true });
      emulatorConnected = true;
    }
    // Skip reCAPTCHA in local dev when using Firebase Console test phone numbers.
    if (
      process.env.NODE_ENV === "development" &&
      process.env.REACT_APP_FIREBASE_USE_EMULATOR !== "true"
    ) {
      auth.settings.appVerificationDisabledForTesting = true;
    }
  }
  return auth;
}

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });

export async function signInWithGoogle() {
  const authInstance = getFirebaseAuth();
  if (isAuthEmulatorEnabled()) {
    const fakeGoogleProfile = encodeURIComponent(
      JSON.stringify({
        sub: `google-${Date.now()}`,
        email: `google-${Date.now()}@proovam.test`,
        email_verified: true,
      })
    );
    const response = await fetch(
      `http://127.0.0.1:9099/identitytoolkit.googleapis.com/v1/accounts:signInWithIdp?key=${firebaseConfig.apiKey}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          postBody: `id_token=${fakeGoogleProfile}&providerId=google.com`,
          requestUri: window.location.origin,
          returnSecureToken: true,
        }),
      }
    );
    const data = await response.json();
    if (!data.idToken) {
      throw new Error(data.error?.message || "Emulator Google sign-in failed");
    }
    return data.idToken;
  }
  const result = await signInWithPopup(authInstance, googleProvider);
  return result.user.getIdToken();
}

export function getPendingGoogleCredential(error) {
  return GoogleAuthProvider.credentialFromError(error);
}

export async function linkGoogleToEmailAccount(email, password, googleCredential) {
  if (!googleCredential) throw new Error("Google credential is unavailable. Try again.");
  const result = await signInWithEmailAndPassword(getFirebaseAuth(), email, password);
  const linked = await linkWithCredential(result.user, googleCredential);
  return linked.user.getIdToken(true);
}

export async function signInWithEmail(email, password) {
  const result = await signInWithEmailAndPassword(getFirebaseAuth(), email, password);
  return result.user.getIdToken();
}

export async function registerWithEmail(email, password) {
  const result = await createUserWithEmailAndPassword(getFirebaseAuth(), email, password);
  return result.user.getIdToken();
}

let recaptchaVerifier = null;

function formatPhoneAuthError(error) {
  const code = error?.code;
  if (code === "auth/too-many-requests") {
    return "Too many attempts. Wait a few minutes and try again.";
  }
  if (code === "auth/captcha-check-failed") {
    return "reCAPTCHA verification failed. Refresh the page and try again.";
  }
  if (code === "auth/invalid-phone-number") {
    return "Invalid phone number for the selected country.";
  }
  if (code === "auth/quota-exceeded") {
    return "SMS quota exceeded. Try again later or use a Firebase test phone number.";
  }
  if (code === "auth/missing-phone-number") {
    return "Enter a valid phone number.";
  }
  return error?.message || "Failed to send verification code";
}

export function resetPhoneRecaptcha() {
  if (!recaptchaVerifier) {
    return;
  }
  try {
    recaptchaVerifier.clear();
  } catch {
    /* ignore */
  }
  recaptchaVerifier = null;
}

export function initPhoneRecaptcha(buttonId = "firebase-send-otp") {
  const authInstance = getFirebaseAuth();
  authInstance.languageCode = "en";

  if (isAuthEmulatorEnabled()) {
    authInstance.settings.appVerificationDisabledForTesting = true;
  }

  resetPhoneRecaptcha();

  recaptchaVerifier = new RecaptchaVerifier(authInstance, buttonId, {
    size: "invisible",
    callback: () => {},
    "expired-callback": () => {
      resetPhoneRecaptcha();
    },
  });

  return recaptchaVerifier;
}

export async function sendPhoneOtp(phoneE164, buttonId = "firebase-send-otp") {
  const authInstance = getFirebaseAuth();
  const verifier = initPhoneRecaptcha(buttonId);

  try {
    await verifier.render();
  } catch {
    /* already rendered */
  }

  try {
    return await signInWithPhoneNumber(authInstance, phoneE164, verifier);
  } catch (error) {
    resetPhoneRecaptcha();
    const wrapped = new Error(formatPhoneAuthError(error));
    wrapped.code = error?.code;
    throw wrapped;
  }
}

export async function confirmPhoneOtp(confirmation, code) {
  const result = await confirmation.confirm(code);
  return result.user.getIdToken();
}
